import { useCallback, useMemo } from "react";

import type { AsyncTask } from "@/modern/components/async-tasks/types";
import {
  useCancelNotificationTask,
  useMarkAllNotificationsSeen,
  useMarkNotificationSeen,
  useNotificationCounts,
  useNotifications,
} from "@/modern/components/notification/api/notifications.api";
import { notificationsService } from "@/modern/components/notification/api/notifications.service";
import type {
  Notification,
  NotificationConfigContext,
  NotificationCountParams,
  NotificationCountsResponse,
  NotificationFilter,
  NotificationSummaryModel,
} from "@/modern/components/notification/types";
import { openExecutedIncentivesEditor, navigateToViewByType } from "@/modern/utils";
import { ALERT_ENTITIES, VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { WIDGET_REGISTRY } from "@/other/constants/EntityManagerConfig";
import { openGeneralWidgetFromGridRecord } from "@/other/utils/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { WidgetParamKeys } from "@/types/entity";

import { mapNotificationCounts, toApiParams } from "../constants/config";
import { buildNotificationSummary } from "../utils";
import { useNotificationScope } from "./useNotificationScope";

export interface UseNotificationPopupOptions {
  filter: NotificationFilter;
  searchQuery: string;
  /** When true, inbox list and counts exclude seen items (Total / All / View all included). */
  hideSeen?: boolean;
  /** When false (summary), skip inbox list — counts alone drive the summary. */
  listEnabled?: boolean;
  closePopup: () => void;
}

const EMPTY_COUNTS: NotificationCountsResponse = {
  total_count: 0,
  unread_count: 0,
  notable_count: 0,
  type_counts: [],
  category_counts: [],
  status_counts: [],
};

export function useNotificationPopup(options: UseNotificationPopupOptions) {
  const { filter, searchQuery, hideSeen = false, listEnabled = true, closePopup } = options;
  const { project, boardManager, user, viewManager, entityManager, editorHandler } =
    useSwivuBryntumContext();
  const scope = useNotificationScope();
  const currentUserId = user?.id ?? undefined;

  const sharedParams = useMemo<NotificationCountParams>(
    () => ({
      board_id: scope.isAccountView ? null : (scope.boardId ?? undefined),
      scope: "all",
      ...(searchQuery.trim() ? { search: searchQuery.trim() } : {}),
    }),
    [scope.boardId, scope.isAccountView, searchQuery],
  );

  const seenParams = useMemo<NotificationCountParams>(
    () => (hideSeen ? { is_seen: false } : {}),
    [hideSeen],
  );

  const listParams = useMemo<NotificationCountParams>(
    () => ({
      ...sharedParams,
      ...toApiParams(filter),
      ...seenParams,
    }),
    [filter, seenParams, sharedParams],
  );

  const countParams = useMemo<NotificationCountParams>(
    () => ({
      ...sharedParams,
      ...seenParams,
    }),
    [seenParams, sharedParams],
  );

  // Single inbox query for the active view filter (including notable). Paginate on scroll — never prefetch all pages.
  const listQuery = useNotifications(listParams, { enabled: listEnabled });
  // Counts follow Hide seen so Total / All / View all / facets match the visible queue.
  const countsQuery = useNotificationCounts(countParams, { enabled: true });
  const markSeenMutation = useMarkNotificationSeen();
  const markAllMutation = useMarkAllNotificationsSeen();
  const cancelMutation = useCancelNotificationTask();

  const notifications = useMemo(() => {
    const loaded = listQuery.data?.pages.flatMap((page) => page.results) ?? [];
    if (filter.kind !== "ids") return loaded;
    const ids = new Set(filter.ids.map(Number));
    return loaded.filter((n) => ids.has(n.id));
  }, [listQuery.data?.pages, filter]);

  const markSeen = useCallback(
    (notification: Notification) => markSeenMutation.mutateAsync({ id: notification.id, is_seen: true }),
    [markSeenMutation],
  );

  const cancelTask = useCallback(
    (asyncTaskId: number) => cancelMutation.mutateAsync(asyncTaskId),
    [cancelMutation],
  );

  const navigate = useCallback(
    (target: { viewType: string | number; viewName: string; otherParams?: Record<string, unknown> }) => {
      navigateToViewByType(viewManager, target.viewType, target.viewName, target.otherParams);
    },
    [viewManager],
  );

  const openNotification = useCallback(
    (notification: Notification, target: Parameters<NonNullable<NotificationConfigContext["openNotification"]>>[1]) => {
      const reminder = project?.reminderStore?.getById?.(notification.id);
      const openEntity = (otherParams?: Record<string, unknown>) => {
        const entityType = reminder?.entityType ?? notification.type;
        const entityId = reminder?.entity?.id ?? notification.task ?? notification.resource ?? notification.client;
        const editor = ALERT_ENTITIES[entityType];
        if (!editor || entityId == null) return false;
        entityManager?.openEntityManager?.({ entityType: editor.entityManagerPage, entityId, ...(otherParams ? { otherParams } : {}) });
        return true;
      };
      switch (target.kind) {
        case "reminder-detail":
          openEntity({ [WidgetParamKeys.REMINDER.EDIT]: Number(reminder?.id ?? notification.id) });
          return;
        case "schedule":
          if (reminder?.entity && openGeneralWidgetFromGridRecord(editorHandler, reminder.entity, WIDGET_REGISTRY.timeline.id, { idSuffix: "timeline-schedule", otherParams: { initialMode: "schedule" } })) return;
          openEntity();
          return;
        case "checklist":
          openEntity(target.checklistId == null ? undefined : { [WidgetParamKeys.CHECKLIST.EDIT]: target.checklistId });
          return;
        case "note":
          openEntity(target.noteId == null ? undefined : { [WidgetParamKeys.NOTE.EDIT]: target.noteId });
          return;
        case "file":
          openEntity(target.fileId == null ? undefined : { [WidgetParamKeys.FILE.EDIT]: target.fileId });
          return;
        case "lead":
          navigateToViewByType(viewManager, VIEW_TYPES.leads, "Leads", { ...(target.leadId != null ? { [WidgetParamKeys.LEADS.EDIT]: target.leadId } : {}) });
          return;
        default:
          openEntity();
      }
    },
    [editorHandler, entityManager, project, viewManager],
  );

  const context = useMemo<NotificationConfigContext>(
    () => ({
      currentUser: currentUserId == null ? null : { id: currentUserId },
      project,
      boardManager,
      scope: {
        kind: scope.isAccountView ? "account" : "board",
        ...(scope.boardId != null ? { boardId: scope.boardId } : {}),
        visibility: "all",
      },
      markSeen,
      cancelTask,
      getResult: notificationsService.getResult,
      navigate,
      openNotification,
      openIncentiveResults: async (notification, result) => {
        if (!notification.async_task) return;
        await openExecutedIncentivesEditor({
          tasks: [notification.async_task as AsyncTask],
          project,
          fetchResult: async () => result,
          markSeen: async () => { await markSeen(notification); },
          editorHandler,
        });
      },
      closePopup,
    }),
    [boardManager, cancelTask, closePopup, currentUserId, editorHandler, markSeen, navigate, openNotification, project, scope.boardId, scope.isAccountView],
  );

  const mappedCounts = useMemo(
    () => mapNotificationCounts(countsQuery.data ?? EMPTY_COUNTS),
    [countsQuery.data],
  );

  const summary: NotificationSummaryModel = useMemo(
    () =>
      buildNotificationSummary(
        notifications,
        [],
        mappedCounts,
        mappedCounts.unread,
      ),
    [mappedCounts, notifications],
  );

  const markAllSeen = useCallback(
    () => markAllMutation.mutateAsync(sharedParams),
    [sharedParams, markAllMutation],
  );

  return {
    notifications,
    context,
    summary,
    mappedCounts,
    hasUnread: summary.unread > 0,
    markAllSeen,
    fetchNextPage: listQuery.fetchNextPage,
    hasNextPage: listQuery.hasNextPage,
    isFetchingNextPage: listQuery.isFetchingNextPage,
    isLoading: countsQuery.isLoading || (listEnabled && listQuery.isLoading),
    isError: countsQuery.isError || (listEnabled && listQuery.isError),
  };
}

export function useNotificationPopupBadgeCount(): number {
  const scope = useNotificationScope();
  const params = useMemo<NotificationCountParams>(
    () => ({
      board_id: scope.isAccountView ? null : (scope.boardId ?? undefined),
      scope: "all",
    }),
    [scope.boardId, scope.isAccountView],
  );
  return useNotificationCounts(params).data?.unread_count ?? 0;
}
